import React from "react";
import SortIcon from "../../Shared/SortIcon/SortIcon";
import { SORT_ORDER } from "../../../models/Sorting";
import { cn } from "../../../utils/cn";

interface SortableThProps<TColumn> {
	/** Header label (string or node, e.g. "From - To"). */
	label: React.ReactNode;
	/** The column this header sorts by. */
	column: TColumn;
	/** The currently sorted column, if any. */
	activeColumn?: TColumn;
	/** The active sort order (only applied when this column is the active one). */
	order: SORT_ORDER;
	/** Called with this column when the header is clicked. */
	onSort: (column: TColumn) => void;
	/** Fixed column width (e.g. "28%" or "90px"). */
	width?: string;
	/** Extra classes on the <th> (e.g. "pl-6" for the first column). */
	className?: string;
	/** Optional resize grab bar rendered on the cell's right edge. */
	resizeHandle?: React.ReactNode;
}

/**
 * A clickable table header cell with a sort indicator, generic over the column
 * enum. Renders the standard list-table header cell styling and shows the
 * {@link SortIcon} reflecting the active order for its column.
 *
 * Shared by the virtualized list pages (jobs, customers, consultants); static
 * (non-sortable) header cells stay plain `<th>` at the call site.
 */
function SortableTh<TColumn>({
	label,
	column,
	activeColumn,
	order,
	onSort,
	width,
	className,
	resizeHandle,
}: SortableThProps<TColumn>) {
	return (
		<th
			className={cn("relative pt-3 pb-2 px-4 cursor-pointer", className)}
			style={width ? { width } : undefined}
			onClick={() => onSort(column)}
		>
			<div className="flex items-center gap-1">
				<span>{label}</span>
				<SortIcon sortType={activeColumn === column ? order : SORT_ORDER.DEFAULT} />
			</div>
			{resizeHandle}
		</th>
	);
}

export default SortableTh;
